import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaArrowLeft } from '@react-icons/all-files/fa/FaArrowLeft';
import { FaSave } from '@react-icons/all-files/fa/FaSave';
import GenericForm, { GenericFormDef, GenericFormAction } from '../commons/GenericForm';
import { isPendienteToPendienteAutorizacion, playSoundDeferred, resolveActividadEstado } from '../shared/audio/audio';
import {
  appendActividadFormValues,
  ejecutarActividadFormAction,
  listarActividadAdjuntos,
  subirActividadAdjunto,
  type IAdjuntoItem,
} from '../shared/workflow/actividad-adjuntos';
import ActividadAdjuntosSection from './ActividadAdjuntosSection';
import { resolveEmpresaContextFromDetail } from './kanban-task-context';
import { type ConfirmConfig } from '../commons/Confirm';

const PLAY_CARD_SOUND = '/assets/audio/play-card.mp3';
const WRONG_SOUND = '/assets/audio/wrong.mp3';

function resolveRequiereArchivoFromDetail(detail: unknown): boolean {
  if (!detail || typeof detail !== 'object') return false;
  const d = detail as Record<string, unknown>;
  const data = d.data as Record<string, unknown> | undefined;
  const nodo = d.nodoInstancia as { requiereArchivo?: unknown } | undefined;
  return !!(d.requiereArchivo ?? data?.requiereArchivo ?? nodo?.requiereArchivo);
}

function resolveValidoParaTerminarFromDetail(detail: unknown): boolean {
  if (!detail || typeof detail !== 'object') return false;
  const d = detail as Record<string, unknown>;
  const data = d.data as Record<string, unknown> | undefined;
  const value = d.validoParaTerminar ?? data?.validoParaTerminar;
  return value === true || value === 'true' || value === 'S' || value === 1 || value === '1';
}

function authHeader(): Record<string, string> {
  const token = localStorage.getItem('jhi-authenticationToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface KanbanFormContext {
  id?: string | number;
  asunto?: string;
  rfc?: string;
  razonSocial?: string;
  grupoEmpresarial?: string;
  workflow?: string;
}

interface KanbanFormProps {
  /** ID of the actividad task */
  taskId: string | number;
  /** Estado de la actividad antes de enviar el formulario */
  sourceEstado?: string;
  /** Id de empresa para obligaciones fiscales */
  empresaId?: string | number;
  /** Read-only context shown above the dynamic form fields */
  context?: KanbanFormContext;
  /** La actividad exige adjuntar un archivo al enviar el formulario */
  requiereArchivo?: boolean;
  /** Ya tiene archivo válido; no exige seleccionar uno nuevo */
  validoParaTerminar?: boolean;
  /** Called after a successful submit */
  onSuccess?: (
    values: Record<string, any>,
    action: GenericFormAction | null,
    meta?: { fromEstado: string; nextEstado: string; transition: boolean; playedTransitionSound: boolean },
  ) => void;
  /** Called when the backend returns an error loading or submitting the form */
  onError?: () => void;
  /** Called when the user cancels */
  onCancel?: () => void;
  /** Solo consulta: formulario y adjuntos sin edición */
  readOnly?: boolean;
  /** Confirmación de adjuntos (p. ej. eliminar) para mostrar en el modal padre */
  onConfirmConfigChange?: (config: ConfirmConfig | null) => void;
}

function KanbanContextReadonlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="kanban-form__inline-field">
      <label className="profase-form-label kanban-form__inline-label">{label}</label>
      <div className="kanban-form__inline-control">
        <div className="profase-form-readonly-value">
          {value || <span style={{ color: 'var(--crm-text-muted)' }}>—</span>}
        </div>
      </div>
    </div>
  );
}

function KanbanContextFields({ context }: { context: KanbanFormContext }) {
  return (
    <div className="kanban-form__context">
      <KanbanContextReadonlyField
        label="ID"
        value={context.id != null ? String(context.id) : ''}
      />
      <KanbanContextReadonlyField label="Asunto" value={context.asunto ?? ''} />
      <KanbanContextReadonlyField label="Grupo empresarial" value={context.grupoEmpresarial ?? ''} />
      <div className="kanban-form__row-2">
        <KanbanContextReadonlyField label="RFC" value={context.rfc ?? ''} />
        <KanbanContextReadonlyField label="Razón social" value={context.razonSocial ?? ''} />
      </div>
      <KanbanContextReadonlyField label="Workflow" value={context.workflow ?? ''} />
    </div>
  );
}

export interface IObligacionFiscalEmpresaItem {
  descripcionObligacion: string;
  descripcionVencimiento: string;
  fechaInicio: string;
}

function formatObligacionFechaInicio(value: string): string {
  if (!value?.trim()) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  return value;
}

function KanbanEmpresaObligacionesPanel({ empresaId }: { empresaId: string | number }) {
  const [items, setItems] = useState<IObligacionFiscalEmpresaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setItems([]);
    fetch(`/api/empresas/${encodeURIComponent(String(empresaId))}/obligacion-fiscal`, {
      headers: { ...authHeader() },
    })
      .then(async res => {
        if (!res.ok) {
          throw new Error(`Error ${res.status} al cargar obligaciones`);
        }
        const data = await res.json();
        if (!cancelled) {
          setItems(Array.isArray(data) ? data : []);
        }
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setError(e?.message ?? 'Error al cargar obligaciones');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [empresaId]);

  return (
    <aside className="kanban-form__obligaciones" aria-label="Obligaciones fiscales">
      <div className="kanban-form__obligaciones-title">Obligaciones fiscales</div>
      {loading ? (
        <div className="kanban-form__obligaciones-placeholder">Cargando…</div>
      ) : error ? (
        <div className="kanban-form__obligaciones-placeholder kanban-form__obligaciones-placeholder--error">
          {error}
        </div>
      ) : items.length === 0 ? (
        <div className="kanban-form__obligaciones-placeholder">Sin obligaciones registradas.</div>
      ) : (
        <div className="kanban-form__obligaciones-table-wrap">
          <table className="kanban-form__obligaciones-table">
            <thead>
              <tr>
                <th>Obligación</th>
                <th>Vencimiento</th>
                <th>Inicio</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={`${item.descripcionObligacion}-${item.fechaInicio}-${index}`}>
                  <td title={item.descripcionObligacion}>{item.descripcionObligacion || '—'}</td>
                  <td title={item.descripcionVencimiento}>{item.descripcionVencimiento || '—'}</td>
                  <td>{formatObligacionFechaInicio(item.fechaInicio)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </aside>
  );
}

export default function KanbanForm({
  taskId,
  sourceEstado,
  context,
  empresaId,
  requiereArchivo: requiereArchivoProp = false,
  validoParaTerminar: validoParaTerminarProp = false,
  onSuccess,
  onError,
  onCancel,
  readOnly = false,
  onConfirmConfigChange,
}: KanbanFormProps) {
  const [formDef, setFormDef] = useState<GenericFormDef | null>(null);
  const [initialValues, setInitialValues] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [requiereArchivo, setRequiereArchivo] = useState(requiereArchivoProp);
  const [validoParaTerminar, setValidoParaTerminar] = useState(validoParaTerminarProp);
  const [adjuntosItems, setAdjuntosItems] = useState<IAdjuntoItem[]>([]);
  const [adjuntosError, setAdjuntosError] = useState<string | null>(null);
  const [adjuntosRefreshKey, setAdjuntosRefreshKey] = useState(0);
  const [resolvedContext, setResolvedContext] = useState<KanbanFormContext | null>(null);

  useEffect(() => {
    setRequiereArchivo(requiereArchivoProp);
    setValidoParaTerminar(validoParaTerminarProp);
    setAdjuntosItems([]);
    setAdjuntosError(null);
    setAdjuntosRefreshKey(0);
    setResolvedContext(null);
  }, [requiereArchivoProp, validoParaTerminarProp, taskId]);

  const fetchForm = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAdjuntosError(null);
    setAdjuntosItems([]);
    try {
      const headers = { ...authHeader() };
      const [defRes, valRes, detailRes] = await Promise.all([
        fetch(`/api/usuario/me/actividad/${taskId}/form`, { headers }),
        fetch(`/api/usuario/me/actividad/${taskId}/form/values`, { headers }),
        fetch(`/api/usuario/me/actividad/${taskId}`, { headers }),
      ]);
      if (!defRes.ok) throw new Error(`Error ${defRes.status} al cargar el formulario`);
      if (!valRes.ok) throw new Error(`Error ${valRes.status} al cargar los valores`);
      const [def, vals] = await Promise.all([defRes.json(), valRes.json()]);
      if (detailRes.ok) {
        const detail = await detailRes.json();
        setRequiereArchivo(resolveRequiereArchivoFromDetail(detail) || requiereArchivoProp);
        setValidoParaTerminar(
          resolveValidoParaTerminarFromDetail(detail) || validoParaTerminarProp,
        );
        const empresaFromDetail = resolveEmpresaContextFromDetail(detail);
        setResolvedContext(empresaFromDetail);
      } else {
        setRequiereArchivo(requiereArchivoProp);
        setValidoParaTerminar(validoParaTerminarProp);
      }
      setFormDef(def);
      setInitialValues(vals ?? {});
    } catch (e: any) {
      setError(e?.message ?? 'Error al cargar el formulario');
      onError?.();
    } finally {
      setLoading(false);
    }
  }, [taskId, requiereArchivoProp, validoParaTerminarProp, onError]);

  useEffect(() => { fetchForm(); }, [fetchForm]);

  const displayContext = useMemo(
    () => ({ ...(context ?? {}), ...(resolvedContext ?? {}) }),
    [context, resolvedContext],
  );

  const handleAdjuntosChange = useCallback((items: IAdjuntoItem[]) => {
    setAdjuntosItems(items);
    setAdjuntosError(null);
    if (items.length > 0) {
      setValidoParaTerminar(true);
    } else if (requiereArchivo) {
      setValidoParaTerminar(false);
    }
  }, [requiereArchivo]);

  const submitAction = formDef?.actions?.find(a => a.type === 'submit') ?? null;
  const uploadActionName = submitAction?.param ?? submitAction?.label ?? 'save';

  const handleSubmit = async (values: Record<string, any>, action: GenericFormAction | null) => {
    const hasAdjuntos = adjuntosItems.length > 0;
    if (requiereArchivo && !hasAdjuntos && !validoParaTerminar) {
      setAdjuntosError('Debe adjuntar al menos un archivo.');
      return;
    }

    setSaving(true);
    setError(null);
    setAdjuntosError(null);
    try {
      const { _files, ...plainValues } = values;
      const actionKey = action?.param ?? action?.label ?? 'save';
      const filesMap: Record<string, File> = { ...(_files as Record<string, File> | undefined) };

      for (const file of Object.values(filesMap)) {
        if (!(file instanceof File)) continue;
        await subirActividadAdjunto(taskId, {
          file,
          nombre: file.name,
          actionName: actionKey,
        });
      }

      const fd = new FormData();
      appendActividadFormValues(fd, plainValues);
      const responseBody = await ejecutarActividadFormAction(taskId, actionKey, fd);

      if (Object.values(filesMap).some(file => file instanceof File)) {
        const adjunto = await listarActividadAdjuntos(taskId);
        const items = adjunto?.items ?? [];
        setAdjuntosItems(items);
        setAdjuntosRefreshKey(prev => prev + 1);
        if (items.length > 0) {
          setValidoParaTerminar(true);
        }
      }

      let nextEstado = resolveActividadEstado(responseBody);

      if (!nextEstado) {
        try {
          const detailRes = await fetch(`/api/usuario/me/actividad/${taskId}`, {
            headers: { ...authHeader() },
          });
          if (detailRes.ok) {
            nextEstado = resolveActividadEstado(await detailRes.json());
          }
        } catch {
          /* ignore */
        }
      }

      const fromEstado = resolveActividadEstado({ estado: sourceEstado });
      const didTransition = Boolean(fromEstado && nextEstado && nextEstado !== fromEstado);
      const pendingAuthTransition = isPendienteToPendienteAutorizacion(fromEstado, nextEstado);
      const shouldPlayTransitionSound = didTransition
        || pendingAuthTransition
        || fromEstado === 'EN_PROCESO'
        || (fromEstado === 'PENDIENTE' && !nextEstado);

      setSuccess(true);
      if (shouldPlayTransitionSound) {
        playSoundDeferred(PLAY_CARD_SOUND);
      }
      onSuccess?.(values, action, {
        fromEstado,
        nextEstado,
        transition: didTransition,
        playedTransitionSound: shouldPlayTransitionSound,
      });
    } catch (e: any) {
      const fromEstado = resolveActividadEstado({ estado: sourceEstado });
      if (fromEstado === 'EN_PROCESO' || fromEstado === 'PENDIENTE' || fromEstado === 'FINALIZADO') {
        playSoundDeferred(WRONG_SOUND);
      }
      setError(e?.message ?? 'Error al enviar el formulario');
      onError?.();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={s.center}>
        <div style={s.spinner} />
        <span style={{ fontSize: 13, color: '#80868b' }}>Cargando formulario…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={s.errorBox}>
        <span>{error}</span>
        <button style={s.retryBtn} onClick={fetchForm}>Reintentar</button>
      </div>
    );
  }

  if (success) {
    return (
      <div style={s.successBox}>
        ✅ Formulario enviado correctamente.
      </div>
    );
  }

  if (!formDef || !initialValues) return null;

  const formId = `kanban-actividad-form-${taskId}`;
  const cancelAction = formDef.actions?.find(a => a.type === 'cancel') ?? null;
  const editableFormDef = readOnly
    ? {
      ...formDef,
      fields: formDef.fields.map(f => ({ ...f, readOnly: true })),
    }
    : formDef;

  return (
    <div className={`kanban-form${readOnly ? ' kanban-form--readonly' : ''}`}>
      <div style={s.sectionTitle}>{readOnly ? 'Detalle de actividad' : 'Formulario de actividad'}</div>
      <div className={`kanban-form__columns${empresaId != null && empresaId !== '' ? ' kanban-form__columns--with-obligaciones' : ''}`}>
        <div className="kanban-form__main">
          <div className="kanban-form__body">
            {context && <KanbanContextFields context={displayContext} />}
            <GenericForm
              formDef={editableFormDef}
              initialValues={initialValues}
              onSubmit={handleSubmit}
              onCancel={() => onCancel?.()}
              saving={saving}
              accentColor="#3b82f6"
              labelInline
              embedded
              hideActions
              formId={formId}
            />
          </div>
        </div>
        {empresaId != null && empresaId !== '' ? (
          <KanbanEmpresaObligacionesPanel empresaId={empresaId} />
        ) : null}
      </div>
      <div className="kanban-form__archivo">
        <ActividadAdjuntosSection
          actividadId={taskId}
          disabled={saving}
          readOnly={readOnly}
          requiereArchivo={requiereArchivo}
          uploadActionName={uploadActionName}
          refreshKey={adjuntosRefreshKey}
          onItemsChange={handleAdjuntosChange}
          onConfirmConfigChange={onConfirmConfigChange}
        />
        {!readOnly && adjuntosError ? (
          <p className="actividad-adjuntos__error">{adjuntosError}</p>
        ) : null}
      </div>
      {!readOnly ? (
      <div className="profase-form-actions kanban-form__actions">
        <button
          type="button"
          className="profase-form-btn-secondary"
          onClick={() => onCancel?.()}
          disabled={saving}
        >
          <FaArrowLeft style={{ marginRight: 6 }} />
          {cancelAction?.label ?? 'Cancelar'}
        </button>
        <button
          type="submit"
          form={formId}
          className="profase-form-btn-primary"
          style={{ background: '#3b82f6' }}
          disabled={saving}
        >
          <FaSave style={{ marginRight: 6 }} />
          {saving ? 'Guardando…' : (submitAction?.label ?? 'Guardar')}
        </button>
      </div>
      ) : null}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  sectionTitle:{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10, flexShrink: 0 },
  center:      { display: 'flex', alignItems: 'center', gap: 10, padding: '16px 0', color: '#80868b', fontSize: 13 },
  spinner:     { width: 18, height: 18, borderRadius: '50%', border: '2px solid #e2e8f0', borderTopColor: '#3b82f6', animation: 'spin 0.7s linear infinite', flexShrink: 0 },
  errorBox:    { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13 },
  retryBtn:    { all: 'unset', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#3b82f6', textDecoration: 'underline' },
  successBox:  { padding: '10px 14px', background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 13 },
};
